import { z } from "zod";

// Recipe-root measurement-vocabulary carrier (Strand 2 DATA).
//
// This is recipe DATA, NOT a meta-schema unit type. It mirrors the printModes[]
// precedent exactly: a recipe declares its OWN measurement vocabulary as a data
// table at the recipe root. The engine holds ZERO unit names — every token, every
// ladder label, every fraction is supplied here by the recipe author. The renderer
// (Plan 02's named-amount formatter) reads its names/tokens/fractions from this
// field; it never branches on a literal unit name.
//
// Shape:
//   - `tokens`: the named-amount table. Each entry maps a label (a friendly unit
//     name, e.g. authored by the recipe) to `num`, an integer count of the recipe's
//     smallest measurement subdivision. The formatter looks up labels by `num` to
//     emit named amounts; collision pairs at equal `num` drive the name-swap
//     alternate the formatter derives.
//   - `ladder`: the decomposition ladder (largest-unit-first). Each tier carries a
//     `num` (its size in the same subdivision unit as `tokens`) and a `granularity`
//     describing the fractions of that tier the formatter may emit: either a bounded
//     array of fraction multipliers (e.g. whole + halves + quarters) OR the literal
//     "any" (the finest tier, which absorbs the entire residual).
//
// isolatedDeclarations (D-15 paranoid-strict tsconfig): the output shape is declared
// as a TS interface and the exported Zod const is annotated `z.ZodType<MeasurementVocabulary>`
// so tsc can emit declarations without reading across module boundaries.
//
// exactOptionalPropertyTypes: there are no optional fields on this carrier (both
// `tokens` and `ladder` are required); the granularity union is modeled directly.
//
// DoS defense-in-depth (T-11.8-DoS-vocab): every array is `.max(...)`-bounded
// (`tokens` ≤ 64, `ladder` ≤ 16, the granularity inner array ≤ 16) and every string
// is `.min(1).max(50)`. An unbounded or oversized vocabulary table is rejected by
// `validateRecipe` before it reaches the storage port (recipe root is z.strictObject,
// so the field is bounded at the source). `num` is a plain `z.number()`: the table is
// a generic carrier and the formatter guards non-finite/out-of-range values itself —
// over-constraining to positive-only here would bake a non-generic assumption into the
// schema.

export interface MeasurementVocabularyToken {
  label: string;
  num: number;
}

export interface MeasurementVocabularyLadderTier {
  label: string;
  num: number;
  granularity: ReadonlyArray<number> | "any";
}

export interface MeasurementVocabulary {
  tokens: ReadonlyArray<MeasurementVocabularyToken>;
  ladder: ReadonlyArray<MeasurementVocabularyLadderTier>;
}

const _tokenSchema = z.strictObject({
  label: z.string().min(1).max(50),
  num: z.number(),
});

const _ladderTierSchema = z.strictObject({
  label: z.string().min(1).max(50),
  num: z.number(),
  // Either a bounded array of fraction multipliers, or "any" (the finest tier).
  granularity: z.union([z.array(z.number()).max(16), z.literal("any")]),
});

export const MeasurementVocabulary: z.ZodType<MeasurementVocabulary> = z.strictObject({
  tokens: z.array(_tokenSchema).max(64),
  ladder: z.array(_ladderTierSchema).max(16),
});
